import { useLongPress } from 'ahooks';
import { useEffect, useRef, useState } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { useExtraTag } from '@/app/hooks';
import type { TagType } from '@/app/types';
import { cn } from '@/app/utils';
import { useDeleteLabelConfirmDialog } from '../hooks/use-delete-label-confirm-dialog';
import type { MaterialV1Label } from '../types';

interface TagItemProps extends TagType {
  isSelected: boolean;
  onClick?: () => void;
  size?: 'small' | 'default';
  className?: string;
  type?: 'tab' | 'label' | 'tag';
  innerClassName?: string;
  bgColor?: string;
  selectedBgColor?: string;
  bgConfig?: {
    unSelect?: string;
    selected?: string;
    hover?: string;
  };
}

export function TagItem({
  name,
  id,
  tag,
  isSelected,
  onClick,
  size = 'default',
  className,
  innerClassName,
  bgConfig,
}: TagItemProps) {
  return (
    <div
      onClick={onClick}
      className={cn(
        'flex max-w-32 cursor-pointer items-center gap-1 rounded-8 font-normal transition-colors',
        isSelected
          ? 'bg-system-white24 font-medium text-white'
          : 'bg-system-white08 text-system-white48 hover:bg-system-white24',
        isSelected && bgConfig?.selected,
        !isSelected && bgConfig?.unSelect,
        !isSelected && bgConfig?.hover,
        size === 'small' ? 'px-4 py-1.5 text-xs' : 'px-4 py-1.5 text-sm sm:px-3 sm:py-1 sm:text-sm',
        className,
      )}
    >
      <CustomTooltip
        content={name}
        side="bottom"
        contentClassName="text-xs text-white"
        showOnlyOnOverflow={true}
        maxLines={1}
        triggerClassName={innerClassName}
        trigger="auto"
      >
        {name}
      </CustomTooltip>

      <span
        className={cn(
          'flex h-[14px] items-center justify-center rounded-4 px-1 text-[10px] backdrop-blur-[2px]',
          (id === 'all' || !tag) && 'hidden',
          isSelected ? 'bg-system-white24 font-semibold text-white' : 'bg-system-white08 text-system-white48',
        )}
      >
        {tag}
      </span>
    </div>
  );
}

export const TagBlock = ({
  tag,
  isSelected,
  onTagSelect,
  className,
  onDelete,
  deleteClassName,
  innerClassName,
  size,
  bgConfig,
  enableDelete,
  wrapperClassName,
}: {
  tag: MaterialV1Label & { tag?: string };
  isSelected: boolean;
  onTagSelect: (value: string, name?: string) => void;
  className?: string;
  onDelete?: (id: string) => void;
  deleteClassName?: string;
  innerClassName?: string;
  wrapperClassName?: string;
  size?: 'small' | 'default';
  enableDelete?: boolean;
  bgConfig?: {
    unSelect?: string;
    selected?: string;
    hover?: string;
  };
}) => {
  const { openDeleteConfirmDialog } = useDeleteLabelConfirmDialog({
    onDelete,
  });
  const { allTag } = useExtraTag();

  const ref = useRef<HTMLDivElement>(null);
  const [longPress, setLongPress] = useState(false);

  useLongPress(
    (e) => {
      if (enableDelete) {
        e.stopPropagation();
        e.preventDefault();
        setLongPress(true);
      }
    },
    ref,
    {
      delay: 500,
    },
  );

  useEffect(() => {
    setLongPress(false);
  }, [isSelected]);

  return (
    <div
      className={cn('group relative', wrapperClassName)}
      ref={ref}
      onContextMenu={(e) => {
        e.preventDefault();
      }}
      onSelect={(e) => {
        e.preventDefault();
      }}
    >
      <TagItem
        name={tag.name}
        id={tag.id}
        tag={tag.tag}
        isSelected={isSelected}
        onClick={() => onTagSelect(tag.id || '', tag.name)}
        className={className}
        innerClassName={innerClassName}
        size={size}
        bgConfig={bgConfig}
      />
      {enableDelete && tag.id !== allTag.id && (
        <div
          className={cn(
            '-top-1 -right-1 absolute hidden h-5 w-5 items-center justify-center rounded-full bg-system-white24 group-hover:flex sm:group-hover:hidden',
            isSelected && 'bg-black/50',
            longPress && 'sm:group-hover:flex',
            deleteClassName,
          )}
          onClick={() => {
            openDeleteConfirmDialog(tag.id ?? '');
          }}
        >
          <BaseLineClose className="text-xs" />
        </div>
      )}
    </div>
  );
};
